import { Ctx } from 'boardgame.io';
import { GameState } from 'Components/AppComponents/Game/Game';
import { POINT_BAR_ANIMATION_DURATION, BOLT_ANIMATION_DURATION, TOWER_ANIMATION_DURATION, POINT_TEXT_ANIMATION_DURATION, BIRD_ANIMATION_DURATION } from 'Constants/AnimationData';
import { BOT_ENERGY_CARD_CORRECT_ANS_DELAY, BOT_ENERGY_CARD_INCORRECT_ANS_DELAY, BOT_REGULAR_CARD_DELAY } from 'Constants/BotData';
import { DELAY_ON_COMPUTER_CARD_DRAW, DIRTY_ENERGY_PERCENTAGE_FOR_PEAK_LOAD_PANIC } from 'Constants/GameData';
import { AudioHelper } from 'Helpers/AudioHelper';
import { GameHelper } from 'Helpers/GameHelper';
import { CardType, CardObjectType } from 'Models/Card';
import { useEffect } from 'react';

type useAnimationPropTypes = {
    isLeft: boolean;
    currentCleanEnergyPercentage: number;
    G: GameState;
    ctx: Ctx;
    events: any;
    prevCurrentCleanEnergyPercentage: number;
    setPrevCurrentCleanEnergyPercentage: Function;
    isAnimationRunning: boolean;
    setIsAnimationRunning: Function;
    showOverlay: boolean;
    showQuestion: boolean;
    isGameOver: boolean;
    setIsGameOver: Function;
    deck: CardObjectType[];
    setDeck: Function;
    setComputerQuestionnaire?: Function;
    pointLog: any;
};

const triggerPointBarAnimation = (id: string, classes: string[]) => {
    const imageTag = document.getElementById(id)! as HTMLImageElement;
    
    if (imageTag) {
        imageTag?.classList.add(...classes);
        setTimeout(() => {
            imageTag!.classList.remove(...classes);
        }, POINT_BAR_ANIMATION_DURATION);
    }
};

const triggerTowerAnimation = (id: string, classes: string[]) => {
    const imageTag = document.getElementById(id)! as HTMLImageElement;
    if (imageTag) {
        imageTag?.classList.add(...classes);
        setTimeout(() => imageTag!.classList.remove(...classes), TOWER_ANIMATION_DURATION);
    }
};

const triggerBoltFadeAnimation = (id: string, classes: string[]) => {
    const imageTag = document.getElementById(id)! as HTMLImageElement;
    if (imageTag) {
        imageTag.classList.add(...classes);
        setTimeout(() => {
            imageTag.classList.remove(...classes);
        }, BOLT_ANIMATION_DURATION);
    }
};

export const triggerPointTextAnimation = (id: string, classes: string[], point: string) => {
    const pTag = document.getElementById(id)! as HTMLParagraphElement;
    if (id.includes('DirtyPointMove')) {
        AudioHelper.playSound('Waster_Dirty_Energy_Points_2nd_sound');
    } else {
        setTimeout(() => {
            AudioHelper.playSound('Saver_Correct_Quiz_Renewable_Energy_card_Points');
        }, 500);        
    }
    if (pTag) {
        pTag!.classList.add(...classes);
        pTag!.textContent = point + 'kW';
        setTimeout(() => {
            pTag!.textContent = '';
            pTag!.classList.remove(...classes);
        }, POINT_TEXT_ANIMATION_DURATION);
    }
};

function animateValue(obj: any, start: number, end: number, duration: number, addedText: string = '', isDirty: boolean = false) {
    let startTimestamp: any = null;
    const step = (timestamp: any) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        obj.innerHTML = isDirty &&  (Math.floor(progress * (end - start) + start) >= DIRTY_ENERGY_PERCENTAGE_FOR_PEAK_LOAD_PANIC) ? 'Peak Load Panic!' : (Math.floor(progress * (end - start) + start) + addedText);
        if (progress < 1) {
            window.requestAnimationFrame(step);
        }
    };
    window.requestAnimationFrame(step);
}

const triggerEnergyChange = (cleanId: string, dirtyId: string, currentCleanEnergyPercentage: number, prevCleanEnergyPercentage: number) => {
    const cleanDivTag = document.getElementById(cleanId)! as HTMLDivElement;
    const dirtyDivTag = document.getElementById(dirtyId)! as HTMLDivElement;
    const currentDirtyPoint = 100 - Math.floor(currentCleanEnergyPercentage);
    const prevDirtyPoint = 100 - Math.floor(prevCleanEnergyPercentage);
    const currentCleanPoint = Math.floor(currentCleanEnergyPercentage);
    const prevCleanPoint = Math.floor(prevCleanEnergyPercentage);
    animateValue(cleanDivTag, prevCleanPoint, currentCleanPoint, 1000, '% Clean Energy');
    animateValue(dirtyDivTag, prevDirtyPoint, currentDirtyPoint, 1000, '% Dirty Energy', true);
    if (currentDirtyPoint >= DIRTY_ENERGY_PERCENTAGE_FOR_PEAK_LOAD_PANIC) {
        if (dirtyId === '1-peak-load') {
            triggerSmokeEmission(['computer-smoke-1', 'computer-smoke-2', 'computer-smoke-3'], ['peakLoadSmokeFadeInAndOut'], currentCleanEnergyPercentage>prevCleanEnergyPercentage );
        } else {
            triggerSmokeEmission(['human-smoke-1', 'human-smoke-2', 'human-smoke-3'], ['peakLoadSmokeFadeInAndOut'], currentCleanEnergyPercentage>prevCleanEnergyPercentage );
        }
    } else {
        if (dirtyId === '1-peak-load') {
            removeSmokeEmission(['computer-smoke-1', 'computer-smoke-2', 'computer-smoke-3'], ['peakLoadSmokeFadeInAndOut']);
        } else {
            removeSmokeEmission(['human-smoke-1', 'human-smoke-2', 'human-smoke-3'], ['peakLoadSmokeFadeInAndOut']);
        }
    }
};

const triggerSmokeEmission = (ids: string[], classes: string[], isClean: boolean = false) => {
    for (const id of ids) {
        if (classes.includes('peakLoadSmokeFadeInAndOut') && !isClean) {
            AudioHelper.playSound('Peak_Load');
        }
        const smokeImg = document.getElementById(id)! as HTMLImageElement;
        smokeImg.classList.add(...classes);
    }
};

const removeSmokeEmission = (ids: string[], classes: string[]) => {
    for (const id of ids) {
        const smokeImg = document.getElementById(id) as HTMLImageElement;
        classes.forEach((clss: string) => {
            if (smokeImg.classList.contains(clss)) smokeImg.classList.remove(clss);
        });
    }
};

const triggerBirdAnimation = (ids: string[], classes: string[]) => {
    for (const id of ids) {
        const birdSvg = document.getElementById(id)!;
        classes.forEach((cls: string) => birdSvg.classList.add(`${cls}-${id}`));
    }
    setTimeout(() => {
        for (const id of ids) {
            const birdSvg = document.getElementById(id)!;
            classes.forEach((cls: string) => birdSvg.classList.remove(`${cls}-${id}`));
        }
    }, BIRD_ANIMATION_DURATION);
};

function useAnimation({ setIsGameOver, isLeft, currentCleanEnergyPercentage, G, ctx, events, prevCurrentCleanEnergyPercentage, setPrevCurrentCleanEnergyPercentage, isAnimationRunning, setIsAnimationRunning, showOverlay, showQuestion, isGameOver, deck, setDeck, pointLog,setComputerQuestionnaire = ()=>{} }: useAnimationPropTypes) {
    
    useEffect(() => {
        if (showOverlay) {
            document.getElementById('board-container')?.classList.add('blur-on');
            setTimeout(() => {
                document.getElementById('human-cards')?.classList.add('zoomCards-hover');
            }, 1000);
        } else {
            document.getElementById('human-cards')?.classList.remove('zoomCards-hover');
            document.getElementById('board-container')?.classList.remove('blur-on');
        }
    }, [showOverlay]);

    useEffect(() => {
        if (showQuestion) {
            document.getElementById('board-container')?.classList.add('blur-on');
        }
    }, [showQuestion]);

    useEffect(() => {
        if (ctx.gameover?.winner) {
            const correctAnswer: boolean = (pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage);
            setTimeout(
                () => {
                    setIsGameOver(true);
                    GameHelper.clearGameData();
                    GameHelper.updatePlayerInfo({replay: true});
                    document.getElementById('footer-container')!.innerText = ctx.gameover?.winner === '0' ? 'You Won!' : 'You lost the game. Better luck next time!';
                },
                ctx.gameover?.winner === '0' ? 2000 : ( G && G.computerLastPlayedCard!.Card.Type !== CardType.Energy? (4000 + BOT_REGULAR_CARD_DELAY) : (13000 + (correctAnswer? BOT_ENERGY_CARD_CORRECT_ANS_DELAY : BOT_ENERGY_CARD_INCORRECT_ANS_DELAY)))
            );
        }
    }, [ctx.gameover]);

    useEffect(() => {
        const correctAnswer: boolean = (pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage);
        setTimeout(
            () => {
                if (document.getElementById('1-blackout-overlay')) {
                    document.getElementById('1-blackout-overlay')!.style.backgroundColor = `rgb(0,0,0, ${GameHelper.calculateCurrentCleanEnergyPercentage(G.computerCleanEnergy) < 50 ? (0.7 / 50) * (50 - GameHelper.calculateCurrentCleanEnergyPercentage(G.computerCleanEnergy)) : 0})`;
                }
            },
            G.computerLastPlayedCard && ctx.currentPlayer === '0' ? (G.computerLastPlayedCard!.Card.Type !== CardType.Energy? 3000 + BOT_REGULAR_CARD_DELAY : (12000 + (correctAnswer? BOT_ENERGY_CARD_CORRECT_ANS_DELAY : BOT_ENERGY_CARD_INCORRECT_ANS_DELAY))) : 1000
        );
    }, [G.computerCleanEnergy]);

    useEffect(() => {
        const correctAnswer: boolean = (pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage);
        setTimeout(
            () => {
                if (document.getElementById('0-blackout-overlay')) {
                    document.getElementById('0-blackout-overlay')!.style.backgroundColor = `rgb(0,0,0, ${GameHelper.calculateCurrentCleanEnergyPercentage(G.humanCleanEnergy) < 50 ? (0.7 / 50) * (50 - GameHelper.calculateCurrentCleanEnergyPercentage(G.humanCleanEnergy)) : 0})`;
                }
            },
            G.computerLastPlayedCard && ctx.currentPlayer === '0' ? (G.computerLastPlayedCard!.Card.Type !== CardType.Energy? 3000 + BOT_REGULAR_CARD_DELAY : (12000 + (correctAnswer? BOT_ENERGY_CARD_CORRECT_ANS_DELAY : BOT_ENERGY_CARD_INCORRECT_ANS_DELAY))) : 1000
        );
    }, [G.humanCleanEnergy]);

    useEffect(() => {
        const tempDeck = deck.filter((x, i) => i !== deck.length - 1);
        const correctAnswer: boolean = (pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage);
        if (G.computerLastPlayedCard && ((ctx.currentPlayer === '0' && !ctx.gameover?.winner) || ctx.gameover?.winner === '1')) {
            if (!GameHelper.getPlayerInfo()?.default) {
                GameHelper.setGameData(G);
            }
            AudioHelper.playSound('Card_and_Answer_Selection');
            document.getElementById('footer-container')!.innerText = GameHelper.computersTurn;
            const idx = G.computerLastPlayedCard?.Index;
            document.getElementById(idx + '-computer-card-img')?.setAttribute('src', G.computerLastPlayedCard.Card.ImageUrl);
            const element = document.getElementById(idx + '-computer-card');
            element?.getElementsByClassName('flip-card-back')[0].children[0]?.setAttribute('src', G.computerLastPlayedCard.Card.ImageUrl);
            element?.classList.add('rotated-computer-card');
            document.getElementById('board-container')?.classList.add('blur-on');            
            setTimeout(() => {
                if(isLeft && G.computerLastPlayedCard!.Card.Type === CardType.Renewable) {
                    AudioHelper.playSound('Renewable_Energy_card_chee_hoo');
                } else if (isLeft && G.computerLastPlayedCard!.Card.Type === CardType.FossilFuel) {
                    AudioHelper.playSound('Fossil_Fuel_card_points_floating_card_in_middle_of_screen_until_points_are_processed');
                }
            }, 300);
            setTimeout(() => {                
                if(isLeft && G.computerLastPlayedCard!.Card.Type === CardType.Energy) {
                    element?.classList.add('half-rotated-computer-card');
                    setComputerQuestionnaire({ 
                        questionnaire : G.computerLastPlayedCard?.Card.Questionnaire,
                        correctAnswer: correctAnswer,
                        showComputerQuestion: true
                    })
                }      

                setTimeout(() => {  
                    element?.classList.add('execute-card');
                    AudioHelper.playSound('Card_Transfer_Deck_to_Across_Screen_slow_down');
                    setTimeout(() => {
                        setComputerQuestionnaire({});                        
                    }, 1000);
                    document.getElementById('board-container')?.classList.remove('blur-on');
                    if (G.computerLastPlayedCard!.Card.Type !== CardType.Energy) {
                        const check = G.computerLastPlayedCard!.Card.Type === CardType.Saver || G.computerLastPlayedCard!.Card.Type === CardType.Renewable;
                        triggerPointTextAnimation(check ? 'computerCleanPointMove' : 'humanDirtyPointMove', [check ? 'computerCleanPointMove' : 'humanDirtyPointMove'], `+${G.computerLastPlayedCard!.Card.CardValue}`);
                        document.getElementById('footer-container')!.innerText = check ? GameHelper.cleanEnergyAddedToGrid('1', G.computerLastPlayedCard!.Card.CardValue) : GameHelper.dirtyEnergyThrownToGrid('1', G.computerLastPlayedCard!.Card.CardValue);
                    } else if (isLeft && document.getElementById('1-point-bar')) {
                        if ((pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage)) {
                            triggerPointTextAnimation('computerCleanPointMove', ['computerCleanPointMove'], `+${Math.abs(pointLog[pointLog.length - 1])}`);
                            document.getElementById('footer-container')!.innerText = GameHelper.quizCorrectAnswerMessage('1', Math.abs(pointLog[pointLog.length - 1]));
                        } else if ((pointLog && pointLog[pointLog.length - 1] < 0) || (prevCurrentCleanEnergyPercentage > currentCleanEnergyPercentage)) {
                            triggerPointTextAnimation('computerDirtyPointMove', ['computerDirtyPointMove'], `+${Math.abs(pointLog[pointLog.length - 1])}`);
                            document.getElementById('footer-container')!.innerText = GameHelper.quizWrongAnswerMessage('1', Math.abs(pointLog[pointLog.length - 1]));
                        }
                    }
                    setTimeout(() => {
                        element?.classList.remove('duration-1000');
                        element?.getElementsByClassName('flip-card-inner')[0].classList.remove('duration-500');
                        element?.classList.remove('half-rotated-computer-card');
                        element?.classList.remove('rotated-computer-card');
                        element?.classList.remove('execute-card');
                        document.getElementById(G.computerLastPlayedCard!.Index + '-computer-card-img')?.setAttribute('src', G.computerCards[idx].ImageUrl);
                        element?.classList.add('card-less-shadow');
                        if (element) {
                            switch (idx) {
                                case 0:
                                    element.style.transform = `translate(${500 + 0.25 * (deck.length - 1)}px, ${9 - 0.25 * (deck.length - 1)}px)`;
                                    break;

                                case 1:
                                    element.style.transform = `translate(${400 + 0.25 * (deck.length - 1)}px, ${9 - 0.25 * (deck.length - 1)}px)`;
                                    break;

                                case 2:
                                    element.style.transform = `translate(${300 + 0.25 * (deck.length - 1)}px, ${9 - 0.25 * (deck.length - 1)}px)`;
                                    break;

                                case 3:
                                    element.style.transform = `translate(${200 + 0.25 * (deck.length - 1)}px, ${9 - 0.25 * (deck.length - 1)}px)`;
                                    break;

                                case 4:
                                    element.style.transform = `translate(${100 + 0.25 * (deck.length - 1)}px, ${9 - 0.25 * (deck.length - 1)}px)`;
                                    break;
                            }
                        }                        
                        setDeck(deck.filter((x, i) => i !== deck.length - 1));
                        setTimeout(() => {
                            AudioHelper.playSound('Card_Flip_2nd_sound');
                            element?.classList.add('duration-1000');
                            element?.classList.remove('card-less-shadow');
                            element?.style.removeProperty('transform');
                            element?.getElementsByClassName('flip-card-inner')[0].classList.add('duration-500');
                        }, 2000);
                    }, 2000);
                }, G.computerLastPlayedCard!.Card.Type !== CardType.Energy? 0 : ((correctAnswer? BOT_ENERGY_CARD_CORRECT_ANS_DELAY : BOT_ENERGY_CARD_INCORRECT_ANS_DELAY) + 8000));
                
            }, G.computerLastPlayedCard!.Card.Type !== CardType.Energy? 1000 + BOT_REGULAR_CARD_DELAY : 2000);
        }
        if (!isGameOver) {
            setTimeout(
                () => {
                    if (isLeft && document.getElementById('1-point-bar')) {
                        let humanPlayedDirtyCard = false;
                        if (G.humanLastPlayedCard) {
                            humanPlayedDirtyCard = [CardType.Waster, CardType.FossilFuel].includes(G.humanLastPlayedCard?.Card.Type);
                        }
                        document.getElementById('1-point-bar')!.style.backgroundPosition = `${currentCleanEnergyPercentage >= 0 ? currentCleanEnergyPercentage : 0}%`;
                        if ((pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage)) {
                            triggerBoltFadeAnimation('computer-positive-label', ['fadeInAndOut', 'pgp-bolt']);
                            triggerTowerAnimation('computer-tower', ['heartBeat']);
                            triggerBirdAnimation(['computer-bird-1', 'computer-bird-2', 'computer-bird-3'], ['birdFly']);
                            triggerPointBarAnimation('1-clean', ['heartBeat']);
                            if (G.computerLastPlayedCard && ctx.currentPlayer === '0') {
                                setTimeout(() => {
                                    if (tempDeck.length > 0) {
                                        setIsAnimationRunning(false);
                                        if(!ctx.gameover?.winner) document.getElementById('footer-container')!.innerText = GameHelper.userTurn;
                                    }
                                }, DELAY_ON_COMPUTER_CARD_DRAW? 3000 : 2000);
                            }
                        } else if ((pointLog && pointLog[pointLog.length - 1] < 0) || (prevCurrentCleanEnergyPercentage > currentCleanEnergyPercentage)) {
                            const dirtyEnergyPercentage = 100 - Math.floor(currentCleanEnergyPercentage);
                            triggerBoltFadeAnimation('computer-negative-label', ['fadeInAndOut', 'pgp-bolt']);
                            triggerPointBarAnimation('1-peak-load', ['shake-0-5']);
                            triggerTowerAnimation('computer-tower', ['shake']);
                            if (dirtyEnergyPercentage < DIRTY_ENERGY_PERCENTAGE_FOR_PEAK_LOAD_PANIC) {
                                triggerSmokeEmission(['computer-smoke-1', 'computer-smoke-2', 'computer-smoke-3'], ['pointDecrementSmokeFadeInAndOut']);
                                setTimeout(() => removeSmokeEmission(['computer-smoke-1', 'computer-smoke-2', 'computer-smoke-3'], ['pointDecrementSmokeFadeInAndOut']), 2000);
                            }
                            if (G.computerLastPlayedCard && ctx.currentPlayer === '0') {
                                setTimeout(() => {
                                    if (tempDeck.length > 0) {
                                        setIsAnimationRunning(false);
                                        if(!ctx.gameover?.winner) document.getElementById('footer-container')!.innerText = GameHelper.userTurn;
                                    }
                                }, DELAY_ON_COMPUTER_CARD_DRAW? 3000 : 2000);
                            }
                        }
                        triggerEnergyChange('1-clean', '1-peak-load', currentCleanEnergyPercentage, prevCurrentCleanEnergyPercentage);
                        if (ctx.currentPlayer === '1' && humanPlayedDirtyCard) {
                        }
                    } else if (!isLeft && document.getElementById('0-point-bar')) {
                        let computerPlayedDirtyCard = false;
                        if (G.computerLastPlayedCard) {
                            computerPlayedDirtyCard = [CardType.Waster, CardType.FossilFuel].includes(G.computerLastPlayedCard?.Card.Type);
                        }
                        document.getElementById('0-point-bar')!.style.backgroundPosition = `${currentCleanEnergyPercentage >= 0 ? currentCleanEnergyPercentage : 0}%`;
                        if ((pointLog && pointLog[pointLog.length - 1] > 0) || (prevCurrentCleanEnergyPercentage < currentCleanEnergyPercentage)) {
                            triggerBoltFadeAnimation('human-positive-label', ['fadeInAndOut', 'pgp-bolt']);
                            triggerPointBarAnimation('0-clean', ['heartBeat']);
                            triggerBirdAnimation(['human-bird-1', 'human-bird-2', 'human-bird-3'], ['birdFly']);
                            triggerTowerAnimation('human-tower', ['heartBeat']);
                            if (G.computerLastPlayedCard && ctx.currentPlayer === '0') {
                                setTimeout(() => {
                                    setIsAnimationRunning(false);
                                    if(!ctx.gameover?.winner) document.getElementById('footer-container')!.innerText = GameHelper.userTurn;
                                }, DELAY_ON_COMPUTER_CARD_DRAW? 3000 : 2000);
                            }
                        } else if ((pointLog && pointLog[pointLog.length - 1] < 0) || (prevCurrentCleanEnergyPercentage > currentCleanEnergyPercentage)) {
                            const dirtyEnergyPercentage = 100 - Math.floor(currentCleanEnergyPercentage);
                            triggerBoltFadeAnimation('human-negative-label', ['fadeInAndOut', 'pgp-bolt']);
                            triggerPointBarAnimation('0-peak-load', ['shake-0-5']);
                            triggerTowerAnimation('human-tower', ['shake']);
                            if (dirtyEnergyPercentage < DIRTY_ENERGY_PERCENTAGE_FOR_PEAK_LOAD_PANIC) {
                                triggerSmokeEmission(['human-smoke-1', 'human-smoke-2', 'human-smoke-3'], ['pointDecrementSmokeFadeInAndOut']);
                                setTimeout(() => removeSmokeEmission(['human-smoke-1', 'human-smoke-2', 'human-smoke-3'], ['pointDecrementSmokeFadeInAndOut']), 2000);
                            }
                            if (G.computerLastPlayedCard && ctx.currentPlayer === '0') {
                                setTimeout(() => {
                                    setIsAnimationRunning(false);
                                    if(!ctx.gameover?.winner) document.getElementById('footer-container')!.innerText = GameHelper.userTurn;
                                }, DELAY_ON_COMPUTER_CARD_DRAW? 3000 : 2000);
                            }
                        }
                        triggerEnergyChange('0-clean', '0-peak-load', currentCleanEnergyPercentage, prevCurrentCleanEnergyPercentage);
                        if (ctx.currentPlayer === '0' && computerPlayedDirtyCard) {
                        }
                    }
                    setPrevCurrentCleanEnergyPercentage(currentCleanEnergyPercentage);
                },
                G.computerLastPlayedCard && ((ctx.currentPlayer === '0' && !ctx.gameover?.winner) || ctx.gameover?.winner === '1') ? ((G.computerLastPlayedCard && G.computerLastPlayedCard!.Card.Type !== CardType.Energy)? BOT_REGULAR_CARD_DELAY + 3000 :((correctAnswer? BOT_ENERGY_CARD_CORRECT_ANS_DELAY : BOT_ENERGY_CARD_INCORRECT_ANS_DELAY) + 12000)) : 1000
            );
        }
    }, [currentCleanEnergyPercentage, pointLog]);  
}

export default useAnimation;
